import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShieldCheck, Car, Users, Wrench, UserCog, LogOut, Menu, X, Sparkles, KeyRound, Building2 } from "lucide-react";
import { ProfiloDialog } from "./ProfiloDialog";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/garanzie", label: "Garanzie", icon: ShieldCheck, id: "garanzie" },
  { to: "/reclami", label: "Reclami & Interventi", icon: Wrench, id: "reclami" },
  { to: "/veicoli", label: "Veicoli", icon: Car, id: "veicoli" },
  { to: "/clienti", label: "Clienti", icon: Users, id: "clienti" },
  { to: "/assistente", label: "Assistente AI", icon: Sparkles, id: "assistente" },
];

export const Brand = ({ dark = true }) => (
  <div className="flex items-center gap-3">
    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#B08D57] font-display text-lg font-bold text-[#D4B98A]">LM</div>
    <div className="leading-tight">
      <div className={`font-display text-lg font-bold tracking-[0.08em] ${dark ? "text-white" : "text-slate-900"}`}>LM AUTO BOTI</div>
      <div className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${dark ? "text-[#D4B98A]" : "text-[#B08D57]"}`}>Garanzie</div>
    </div>
  </div>
);

const Sidebar = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [profilo, setProfilo] = useState(false);
  const items = user?.role === "admin" ? [...NAV, { to: "/utenti", label: "Utenti", icon: UserCog, id: "utenti" }, { to: "/impostazioni", label: "Dati concessionaria", icon: Building2, id: "impostazioni" }] : NAV;
  return (
    <div className="flex h-full flex-col bg-[#0F1B2D] text-slate-300">
      <div className="border-b border-white/10 px-5 py-5"><Brand /></div>
      <nav className="flex-1 space-y-1 px-3 py-5">
        {items.map(({ to, label, icon: Icon, id }) => (
          <NavLink
            key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "border-l-2 border-[#B08D57] bg-white/[0.06] text-[#E9DCC4]" : "border-l-2 border-transparent hover:bg-white/5 hover:text-white"}`}
          >
            <Icon className="h-4 w-4" strokeWidth={2} /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="mb-3 truncate text-sm font-semibold text-white" data-testid="current-user-name">{user?.name}</div>
        <div className="mb-3 truncate text-xs text-slate-400">{user?.email}</div>
        <button
          data-testid="profilo-button"
          onClick={() => setProfilo(true)}
          className="mb-2 flex w-full items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm transition-colors hover:bg-white/5 hover:text-white"
        >
          <KeyRound className="h-4 w-4" /> Il mio profilo
        </button>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); nav("/login"); }}
          className="flex w-full items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Esci
        </button>
      </div>
      <ProfiloDialog open={profilo} onClose={() => setProfilo(false)} />
    </div>
  );
};

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block"><Sidebar /></aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64"><Sidebar onNavigate={() => setOpen(false)} /></div>
          <button className="absolute right-4 top-4 text-white" onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X /></button>
        </div>
      )}
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b bg-white/80 px-4 py-3 backdrop-blur-md lg:hidden">
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-open"><Menu /></button>
        <span className="font-display font-extrabold">LM AUTO BOTI</span>
      </header>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-8 sm:py-10"><Outlet /></div>
      </main>
    </div>
  );
}

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="rise mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#B08D57]">LM Auto Boti</div>
      <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
